import React, { useEffect } from 'react';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import CheckboxCircleFillIcon from 'remixicon-react/CheckboxCircleFillIcon';
import { useForm } from 'react-hook-form';
import ModalContainer from '../../atoms/Modal/ModalContainer';
import HeaderTitle from '../../atoms/Modal/HeaderTitle';
import FooterButtonContainer from '../../atoms/Modal/FooterButtonContainer';
import Button from '../../atoms/Button/Button';
import Card from '../../atoms/Card/Card';
import Flex from '../../atoms/Box/Flex';
import Text from '../../atoms/Text/Text';
import InformationMessage from '../../atoms/Message/Information/InformationMessage';
import Checkbox from '../../atoms/Checkbox/Checkbox';
import GridInput from '../../atoms/Input/GridInput';
import FormRow from '../../atoms/Form/FormRow';
import Spinner from '../../atoms/Spinner/Spinner';
import Loader from '../../atoms/SkeletonLoader/Loader';
import ErrorMessage from '../../atoms/Message/Error/ErrorMessage';
import { ErrorType } from '../../../types/response/ErrorCodes';
import { emailValidationConfig } from '../../../types/ValidationTypes';
import { getMerchantKey, hasMerchantName, Merchant } from '../../../types/onboarding/Merchant';
import {
    AgreementSignatory,
    signatoryDisplayName,
} from '../../../types/onboarding/MerchantAgreement';
import { useOnboardingStore } from '../../../store/admin/onboarding/onboardingStore';
import {
    listAgreementSignatories,
    sendMerchantAgreement,
} from '../../../api/onboarding/merchantAgreements';
import { formatOrganizationNumber } from '../../../services/helpers/organizationNumber';

interface SendAgreementModalProps {
    organizationId: string;
    merchant: Merchant;
    hide: () => void;
    theme: DefaultTheme;
}

/** One person from the register: whether they are to sign, and where the agreement is emailed when they are. */
interface RegisteredFields {
    selected: boolean;
    email: string;
}

interface Inputs {
    registered: RegisteredFields[];
}

const isValidEmail = (email: string) => emailValidationConfig.emailRegExp.test(email.trim());

/**
 * Why an email address cannot be used, or undefined when it can. An empty address is a problem that only blocks
 * sending; a message is shown for an address that is typed but wrong, or used by another signatory.
 */
const emailProblem = (email: string, allEmails: string[]): string | undefined => {
    const address = email.trim().toLowerCase();

    if (address === '') return 'Required';
    if (!isValidEmail(address)) return 'Enter a valid email address';
    if (allEmails.filter(other => other.trim().toLowerCase() === address).length > 1)
        return 'Another signatory already has this email address';

    return undefined;
};

/** Whether the problem is shown under the field: an empty address is only pointed out by the disabled send button. */
const isShown = (problem: string | undefined) => problem !== undefined && problem !== 'Required';

/**
 * Sends the agreement of a merchant for e-signing. The people the business register lists as signatories of the
 * merchant are offered to pick from, each with an email address to send the agreement to. Sending posts them to the
 * API, which puts the agreement in Scrive and emails it to each of them. The merchant then shows the agreement as
 * pending until everyone has signed. When the register lists no one, the agreement cannot be sent from here.
 */
const SendAgreementModal: React.FC<SendAgreementModalProps> = ({
    organizationId,
    merchant,
    hide,
    theme,
}: SendAgreementModalProps) => {
    const {
        agreementMerchantKey,
        signatories,
        isLoadingSignatories,
        signatoriesError,
        isSendingAgreement,
        agreementSent,
        sendAgreementError,
        loadSignatoriesStarted,
        loadSignatoriesSucceeded,
        loadSignatoriesFailed,
        sendAgreementStarted,
        sendAgreementSucceeded,
        sendAgreementFailed,
    } = useOnboardingStore();

    const { register, handleSubmit, reset, watch } = useForm<Inputs>({
        mode: 'onChange',
        defaultValues: { registered: [] },
    });

    // Who may sign is fetched from the register when the modal opens (GET .../agreements/signatories).
    useEffect(() => {
        listAgreementSignatories(
            organizationId,
            merchant,
            loadSignatoriesStarted,
            loadSignatoriesSucceeded,
            loadSignatoriesFailed
        );
    }, [
        organizationId,
        merchant,
        loadSignatoriesStarted,
        loadSignatoriesSucceeded,
        loadSignatoriesFailed,
    ]);

    const isLoaded =
        agreementMerchantKey === getMerchantKey(merchant) &&
        !isLoadingSignatories &&
        !signatoriesError;
    const isRegisterEmpty = isLoaded && signatories.length === 0;
    const canFillIn = isLoaded && !isRegisterEmpty;

    // The loaded register fills the form with one unselected row per person.
    useEffect(() => {
        if (!isLoaded) return;

        reset({ registered: signatories.map(() => ({ selected: false, email: '' })) });
    }, [isLoaded, signatories, reset]);

    const registered = watch('registered') ?? [];

    const selectedRows = registered.filter(row => row.selected);
    const allEmails = selectedRows.map(row => row.email);
    const hasProblems = selectedRows.some(row => emailProblem(row.email, allEmails) !== undefined);
    const isSent = agreementSent;
    const canSend =
        canFillIn && selectedRows.length > 0 && !hasProblems && !isSendingAgreement && !isSent;

    const onSubmit = (data: Inputs) => {
        const picked = data.registered.flatMap((row, index): AgreementSignatory[] => {
            if (!row.selected) return [];

            const person = signatories[index];

            return [
                {
                    name: person.fullName,
                    email: row.email.trim(),
                    ...(person.personalId ? { personalId: person.personalId } : {}),
                },
            ];
        });

        sendMerchantAgreement(
            organizationId,
            merchant,
            { signatories: picked },
            sendAgreementStarted,
            sendAgreementSucceeded,
            sendAgreementFailed
        );
    };

    const merchantName = hasMerchantName(merchant)
        ? merchant.name
        : formatOrganizationNumber(merchant.countryCode, merchant.organizationNumber);

    const renderSignatory = (person: (typeof signatories)[number], index: number) => {
        const selected = registered[index]?.selected ?? false;
        const selectedField = register(`registered.${index}.selected`);
        const emailField = register(`registered.${index}.email`);
        const checkboxId = `signatory-${index}-selected`;
        const emailId = `signatory-${index}-email`;
        const problem = emailProblem(registered[index]?.email ?? '', allEmails);
        // Only a person the register has a personal ID number for can be picked to sign.
        const canSign = !!person.personalId;

        return (
            <SignatoryCard
                key={`${person.fullName}-${person.personalId ?? ''}`}
                dataTestId={`signatory-${index}`}
                borderColor={selected ? theme.colors.primary : undefined}
            >
                <SignatoryFields>
                    <SignatoryRow>
                        <CheckboxBox>
                            <Checkbox
                                id={checkboxId}
                                name={selectedField.name}
                                setRef={selectedField.ref}
                                onChange={selectedField.onChange}
                                onBlur={selectedField.onBlur}
                                dataTestId={checkboxId}
                                disabled={!canSign}
                                noMargin
                            />
                        </CheckboxBox>
                        <SignatoryName>
                            <Text as="label" htmlFor={checkboxId} medium clickable={canSign}>
                                {signatoryDisplayName(person.fullName)}
                            </Text>
                        </SignatoryName>
                        {person.personalId ? (
                            <Text secondary tabular nowrap>
                                {person.personalId}
                            </Text>
                        ) : (
                            <Text
                                data-testid={`signatory-${index}-missing-id`}
                                accent
                                small
                                medium
                                nowrap
                            >
                                Personal ID number missing
                            </Text>
                        )}
                    </SignatoryRow>
                    {selected && (
                        <FormRow first last>
                            <GridInput
                                type="email"
                                id={emailId}
                                placeholder="Email address"
                                setRef={emailField.ref}
                                name={emailField.name}
                                onChange={emailField.onChange}
                                validationError={isShown(problem)}
                                validationErrorText={isShown(problem) ? problem : undefined}
                                dataTestId={emailId}
                            />
                        </FormRow>
                    )}
                </SignatoryFields>
            </SignatoryCard>
        );
    };

    /** The agreement is on its way; the form is gone so that nothing is sent twice. */
    const renderSent = () => (
        <SentMessage data-testid="agreement-sent" role="status">
            <CheckboxCircleFillIcon color={theme.colors.success} size={theme.icon.size.large} />
            <Text as="p">
                The agreement was emailed to {selectedRows.length}{' '}
                {selectedRows.length === 1 ? 'signatory' : 'signatories'} for signing. The merchant
                shows the agreement as signed once everyone has signed.
            </Text>
        </SentMessage>
    );

    const renderContent = () => {
        if (isSent) return renderSent();

        // The register is being read: the shape of the list to come, rather than a spinner.
        if (isLoadingSignatories)
            return (
                <SignatoryList data-testid="signatories-loading">
                    <ListHeader>
                        <Loader width="14rem" height="1.8rem" round />
                        <Loader width="8rem" height="1.4rem" round />
                    </ListHeader>
                    {[0, 1].map(index => (
                        <SignatoryCard key={index}>
                            <SignatoryRow>
                                <Loader width="1.8rem" height="1.8rem" round />
                                <Loader width="16rem" height="1.8rem" round />
                                <Loader width="9rem" height="1.4rem" round />
                            </SignatoryRow>
                        </SignatoryCard>
                    ))}
                </SignatoryList>
            );

        if (signatoriesError)
            return (
                <ErrorMessage
                    error={signatoriesError}
                    errorHeader={ErrorType.LoadAgreementSignatories}
                />
            );

        if (!isLoaded) return null;

        // The register lists no one who may sign, so there is nothing to fill in.
        if (isRegisterEmpty)
            return (
                <InformationMessage
                    dataTestId="register-empty"
                    message="The business register lists no one who may sign for this company, so the agreement cannot be sent."
                />
            );

        return (
            <SignatoryList
                as="form"
                id="send-agreement-form"
                data-testid="send-agreement-form"
                onSubmit={handleSubmit(onSubmit)}
            >
                <ListHeader>
                    <ListTitle>
                        <Text medium>Signatories</Text>
                        <Text secondary small>
                            Select who should sign
                        </Text>
                    </ListTitle>
                    <Text data-testid="signatories-counter" secondary small nowrap>
                        {selectedRows.length} of {signatories.length} selected
                    </Text>
                </ListHeader>

                {signatories.map(renderSignatory)}
            </SignatoryList>
        );
    };

    return (
        <>
            <ModalContainer position="header">
                <HeaderTitle>Send merchant agreement</HeaderTitle>
                <Text secondary>{merchantName}</Text>
            </ModalContainer>

            <ModalContainer position="content">
                <Content>
                    <Text as="p" secondary>
                        We&apos;ll email the agreement to each person you select.
                    </Text>
                    {renderContent()}
                </Content>
            </ModalContainer>

            {sendAgreementError && (
                <ModalContainer position="error">
                    <div data-testid="send-agreement-error">
                        <ErrorMessage
                            error={sendAgreementError}
                            errorHeader={ErrorType.SendAgreement}
                        />
                    </div>
                </ModalContainer>
            )}

            <ModalContainer position="footer">
                <Footer>
                    {canFillIn && !isSent && (
                        <Text secondary small>
                            Select who should sign and add a valid email address for each.
                        </Text>
                    )}
                    <FooterButtonContainer>
                        <Button
                            type="button"
                            large
                            disabled={isSendingAgreement}
                            data-testid="close-agreement-button"
                            onClick={hide}
                        >
                            {isSent ? 'Close' : 'Cancel'}
                        </Button>
                        {canFillIn && !isSent && (
                            <Button
                                type="submit"
                                form="send-agreement-form"
                                primary
                                large
                                disabled={!canSend}
                                data-testid="send-agreement-button"
                            >
                                {isSendingAgreement ? (
                                    <Spinner color={theme.colors.light} size={8} loading />
                                ) : (
                                    'Send agreement'
                                )}
                            </Button>
                        )}
                    </FooterButtonContainer>
                </Footer>
            </ModalContainer>
        </>
    );
};

/** The intro and what the register gave, stacked. */
const Content = styled(Flex).attrs(props => ({
    column: true,
    gap: props.theme.layout.gap.medium,
}))``;

/** The check icon and the sent message side by side, the icon kept at its size. */
const SentMessage = styled(Flex).attrs(props => ({
    alignItems: 'center',
    gap: props.theme.layout.gap.small,
}))`
    svg {
        flex: none;
    }
`;

/** The header and the cards of the signatories, stacked; the form when the register is loaded. */
const SignatoryList = styled(Flex).attrs(props => ({
    column: true,
    gap: props.theme.layout.gap.small,
}))``;

/** The title of the list to the left and the counter to the right. */
const ListHeader = styled(Flex).attrs(props => ({
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: props.theme.layout.gap.small,
}))``;

/** The title of the list and its hint, side by side. */
const ListTitle = styled(Flex).attrs(props => ({
    alignItems: 'baseline',
    gap: props.theme.layout.gap.small,
}))``;

/** The row of a person and the fields below it when they are picked, stacked. */
const SignatoryFields = styled(Flex).attrs(props => ({
    column: true,
    gap: props.theme.layout.gap.xsmall,
}))``;

/** The checkbox, name and personal ID number of a person on one line. */
const SignatoryRow = styled(Flex).attrs(props => ({
    alignItems: 'center',
    gap: props.theme.layout.gap.xsmall,
}))``;

/** The name takes the room between the checkbox and the personal ID number. */
const SignatoryName = styled(Flex).attrs({ grow: 1 })``;

/** The hint to the left and the buttons to the right. */
const Footer = styled(Flex).attrs(props => ({
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: props.theme.layout.gap.small,
}))``;

/** A card per person; a selected one gets the primary border through the card's borderColor. */
const SignatoryCard = styled(Card)`
    padding: ${props => props.theme.layout.padding.small}
        ${props => props.theme.layout.padding.medium};
`;

/** The checkbox draws its 2rem box from the top of an empty label, so a box of that size keeps it centered on the row. */
const CheckboxBox = styled.div`
    display: flex;
    width: 2rem;
    height: 2rem;

    & > label {
        height: 2rem;
    }
`;

export default withTheme(SendAgreementModal) as React.ComponentType<
    Omit<SendAgreementModalProps, 'theme'>
>;
